// Pre-defined color palette for outcomes - hardcoded for non-simulation version
export const OUTCOME_COLORS = [
  { primary: "#007A33", secondary: "#FFFFFF" }, // Green
  { primary: "#006BB6", secondary: "#F58426" }, // Blue/Orange
  { primary: "#552583", secondary: "#FDB927" }, // Purple/Gold
  { primary: "#CE1141", secondary: "#FFFFFF" }, // Red
  { primary: "#1D1160", secondary: "#FFFFFF" }, // Dark Blue
  { primary: "#C8102E", secondary: "#FFFFFF" }, // Crimson
  { primary: "#FF6B35", secondary: "#FFFFFF" }, // Orange
  { primary: "#8B5CF6", secondary: "#FFFFFF" }, // Purple
  { primary: "#10B981", secondary: "#FFFFFF" }, // Emerald
  { primary: "#F59E0B", secondary: "#FFFFFF" }, // Amber
  { primary: "#EF4444", secondary: "#FFFFFF" }, // Red
  { primary: "#3B82F6", secondary: "#FFFFFF" }, // Blue
] as const

// Manual color overrides for specific outcomes
// Add new overrides here by adding entries to this object
// The key should be the exact outcome name (case-insensitive matching is supported)
// Colors should follow team/sport branding when possible
export const OUTCOME_COLOR_OVERRIDES: Record<string, { primary: string; secondary: string }> = {
  // Draw outcomes - Gray color
  draw: { primary: "#666666", secondary: "#FFFFFF" },

  // F1 Drivers - 2025 Season
  // Ferrari (Red/Yellow)
  "charles leclerc": { primary: "#DC143C", secondary: "#FFFF00" },
  "lewis hamilton": { primary: "#DC143C", secondary: "#FFFF00" },

  // Mercedes (Silver/Teal)
  "george russell": { primary: "#00D2BE", secondary: "#C0C0C0" },
  "kimi antonelli": { primary: "#00D2BE", secondary: "#C0C0C0" },

  // Red Bull Racing (Blue/Red)
  "max verstappen": { primary: "#0600EF", secondary: "#DC143C" },
  "yuki tsunoda": { primary: "#0600EF", secondary: "#DC143C" },

  // McLaren (Orange/Black)
  "lando norris": { primary: "#FF8700", secondary: "#000000" },
  "oscar piastri": { primary: "#FF8700", secondary: "#000000" },

  // Aston Martin (Green/Pink)
  "fernando alonso": { primary: "#006F62", secondary: "#FF69B4" },
  "lance stroll": { primary: "#006F62", secondary: "#FF69B4" },

  // Williams (Blue/White)
  "alex albon": { primary: "#1E41FF", secondary: "#FFFFFF" },
  "carlos sainz": { primary: "#1E41FF", secondary: "#FFFFFF" },

  // RB/Racing Bulls (Blue/Red)
  "liam lawson": { primary: "#0600EF", secondary: "#DC143C" },
  "isack hadjar": { primary: "#0600EF", secondary: "#DC143C" },

  // Haas (White/Red)
  "esteban ocon": { primary: "#FFFFFF", secondary: "#DC143C" },
  "oliver bearman": { primary: "#FFFFFF", secondary: "#DC143C" },

  // Kick Sauber (Green/White)
  "nico hulkenberg": { primary: "#00FF00", secondary: "#FFFFFF" },
  "gabriel bortoleto": { primary: "#00FF00", secondary: "#FFFFFF" },

  // Tennis Players - US Open 2025
  "novak djokovic": { primary: "#0c2a89", secondary: "#f7d548" },
  "carlos alcaraz": { primary: "#0c2a89", secondary: "#f7d548" },
  "jannik sinner": { primary: "#0c2a89", secondary: "#f7d548" },
  "daniil medvedev": { primary: "#0c2a89", secondary: "#f7d548" },
  "ben shelton": { primary: "#0c2a89", secondary: "#f7d548" },
  "taylor fritz": { primary: "#0c2a89", secondary: "#f7d548" },
  "rest of field": { primary: "#6B7280", secondary: "#E5E7EB" },

  // Women's Tennis Players - US Open 2025
  "iga swiatek": { primary: "#0c2a89", secondary: "#f7d548" },
  "aryna sabalenka": { primary: "#0c2a89", secondary: "#f7d548" },
  "coco gauff": { primary: "#0c2a89", secondary: "#f7d548" },
  "elena rybakina": { primary: "#0c2a89", secondary: "#f7d548" },
  "jessica pegula": { primary: "#0c2a89", secondary: "#f7d548" },
  "ons jabeur": { primary: "#0c2a89", secondary: "#f7d548" },
  "emma raducanu": { primary: "#0c2a89", secondary: "#f7d548" },
  "naomi osaka": { primary: "#0c2a89", secondary: "#f7d548" },

  // EPL Teams
  arsenal: { primary: "#EF0107", secondary: "#FFFFFF" },
  liverpool: { primary: "#C8102E", secondary: "#F6EB61" },

  // NFL Teams - All 32 Teams (from dummyData)
  // AFC East
  "buffalo bills": { primary: "#00338D", secondary: "#C60C30" },
  bills: { primary: "#00338D", secondary: "#C60C30" },
  "miami dolphins": { primary: "#008E97", secondary: "#FC4C02" },
  dolphins: { primary: "#008E97", secondary: "#FC4C02" },
  "new england patriots": { primary: "#002244", secondary: "#C60C30" },
  patriots: { primary: "#002244", secondary: "#C60C30" },
  "new york jets": { primary: "#125740", secondary: "#FFFFFF" },
  jets: { primary: "#125740", secondary: "#FFFFFF" },

  // Additional duplicates for common variations
  buffalo: { primary: "#00338D", secondary: "#C60C30" }, // For Buffalo teams
  miami: { primary: "#008E97", secondary: "#FC4C02" }, // For Miami teams
  "new england": { primary: "#002244", secondary: "#C60C30" }, // For New England teams
  "new york": { primary: "#125740", secondary: "#FFFFFF" }, // For New York teams

  // AFC North
  "baltimore ravens": { primary: "#241773", secondary: "#9E7C0C" },
  ravens: { primary: "#241773", secondary: "#9E7C0C" },
  "cincinnati bengals": { primary: "#FB4F14", secondary: "#000000" },
  bengals: { primary: "#FB4F14", secondary: "#000000" },
  "cleveland browns": { primary: "#311D00", secondary: "#FF3C00" },
  browns: { primary: "#311D00", secondary: "#FF3C00" },
  "pittsburgh steelers": { primary: "#FFB612", secondary: "#101820" },
  steelers: { primary: "#FFB612", secondary: "#101820" },

  // Additional duplicates for common variations
  baltimore: { primary: "#241773", secondary: "#9E7C0C" }, // For Baltimore teams
  cincinnati: { primary: "#FB4F14", secondary: "#000000" }, // For Cincinnati teams
  cleveland: { primary: "#311D00", secondary: "#FF3C00" }, // For Cleveland teams
  pittsburgh: { primary: "#FFB612", secondary: "#101820" }, // For Pittsburgh teams

  // AFC South
  "houston texans": { primary: "#03202F", secondary: "#A71930" },
  texans: { primary: "#03202F", secondary: "#A71930" },
  "indianapolis colts": { primary: "#002C5F", secondary: "#A2AAAD" },
  colts: { primary: "#002C5F", secondary: "#A2AAAD" },
  "jacksonville jaguars": { primary: "#006778", secondary: "#9F792C" },
  jaguars: { primary: "#006778", secondary: "#9F792C" },
  "tennessee titans": { primary: "#0C2340", secondary: "#4B92DB" },
  titans: { primary: "#0C2340", secondary: "#4B92DB" },

  // Additional duplicates for common variations
  houston: { primary: "#03202F", secondary: "#A71930" }, // For Houston teams
  indianapolis: { primary: "#002C5F", secondary: "#A2AAAD" }, // For Indianapolis teams
  jacksonville: { primary: "#006778", secondary: "#9F792C" }, // For Jacksonville teams
  tennessee: { primary: "#0C2340", secondary: "#4B92DB" }, // For Tennessee teams

  // AFC West
  "kansas city chiefs": { primary: "#E31837", secondary: "#FFB81C" },
  chiefs: { primary: "#E31837", secondary: "#FFB81C" },
  "denver broncos": { primary: "#FB4F14", secondary: "#002244" },
  broncos: { primary: "#FB4F14", secondary: "#002244" },
  "las vegas raiders": { primary: "#000000", secondary: "#A5ACAF" },
  raiders: { primary: "#000000", secondary: "#A5ACAF" },
  "los angeles chargers": { primary: "#0080C6", secondary: "#FFC20E" },
  chargers: { primary: "#0080C6", secondary: "#FFC20E" },

  // Additional duplicates for common variations
  "kansas city": { primary: "#E31837", secondary: "#FFB81C" }, // For Kansas City teams
  denver: { primary: "#FB4F14", secondary: "#002244" }, // For Denver teams
  "las vegas": { primary: "#000000", secondary: "#A5ACAF" }, // For Las Vegas teams
  vegas: { primary: "#000000", secondary: "#A5ACAF" }, // For Vegas teams

  // NFC East
  "dallas cowboys": { primary: "#041E42", secondary: "#869397" },
  cowboys: { primary: "#041E42", secondary: "#869397" },
  "new york giants": { primary: "#0B2265", secondary: "#A71930" },
  giants: { primary: "#0B2265", secondary: "#A71930" },
  "philadelphia eagles": { primary: "#004C54", secondary: "#FFFFFF" },
  eagles: { primary: "#004C54", secondary: "#FFFFFF" },
  "washington commanders": { primary: "#5A1414", secondary: "#FFB612" },
  commanders: { primary: "#5A1414", secondary: "#FFB612" },

  // Additional duplicates for common variations
  dallas: { primary: "#041E42", secondary: "#869397" }, // For Dallas teams
  philadelphia: { primary: "#004C54", secondary: "#A5ACAF" }, // For Philadelphia teams
  washington: { primary: "#5A1414", secondary: "#FFB612" }, // For Washington teams

  // NFC North
  "chicago bears": { primary: "#0B162A", secondary: "#C83803" },
  bears: { primary: "#0B162A", secondary: "#C83803" },
  "detroit lions": { primary: "#0076B6", secondary: "#B0B7BC" },
  lions: { primary: "#0076B6", secondary: "#B0B7BC" },
  "green bay packers": { primary: "#203731", secondary: "#FFB612" },
  packers: { primary: "#203731", secondary: "#FFB612" },
  "minnesota vikings": { primary: "#4F2683", secondary: "#FFC62F" },
  vikings: { primary: "#4F2683", secondary: "#FFC62F" },

  // Additional duplicates for common variations
  chicago: { primary: "#0B162A", secondary: "#C83803" }, // For Chicago teams
  detroit: { primary: "#0076B6", secondary: "#B0B7BC" }, // For Detroit teams
  "green bay": { primary: "#203731", secondary: "#FFB612" }, // For Green Bay teams
  minnesota: { primary: "#4F2683", secondary: "#FFC62F" }, // For Minnesota teams

  // NFC South
  "atlanta falcons": { primary: "#A71930", secondary: "#000000" },
  falcons: { primary: "#A71930", secondary: "#000000" },
  "carolina panthers": { primary: "#0085CA", secondary: "#101820" },
  panthers: { primary: "#0085CA", secondary: "#101820" },
  "carolina hurricanes": { primary: "#CC0000", secondary: "#000000" }, // Add missing Carolina Hurricanes colors
  "new orleans saints": { primary: "#D3BC8D", secondary: "#101820" },
  saints: { primary: "#D3BC8D", secondary: "#101820" },
  "tampa bay buccaneers": { primary: "#D50A0A", secondary: "#FF7900" },
  buccaneers: { primary: "#D50A0A", secondary: "#FF7900" },

  // Additional duplicates for common variations
  atlanta: { primary: "#A71930", secondary: "#000000" }, // For Atlanta teams
  "new orleans": { primary: "#D3BC8D", secondary: "#101820" }, // For New Orleans teams
  "tampa bay": { primary: "#D50A0A", secondary: "#FF7900" }, // For Tampa Bay teams
  tampa: { primary: "#D50A0A", secondary: "#FF7900" }, // For Tampa teams

  // NFC West
  "arizona cardinals": { primary: "#97233F", secondary: "#000000" },
  cardinals: { primary: "#97233F", secondary: "#000000" },
  "los angeles rams": { primary: "#003594", secondary: "#FFA300" },
  rams: { primary: "#003594", secondary: "#FFA300" },
  "san francisco 49ers": { primary: "#AA0000", secondary: "#B3995D" },
  "49ers": { primary: "#AA0000", secondary: "#B3995D" },
  "seattle seahawks": { primary: "#002244", secondary: "#69BE28" },
  seahawks: { primary: "#002244", secondary: "#69BE28" },

  // Additional duplicates for common variations
  "los angeles": { primary: "#003594", secondary: "#FFA300" }, // For LA teams
  "san francisco": { primary: "#AA0000", secondary: "#B3995D" }, // For San Francisco teams
  seattle: { primary: "#002244", secondary: "#69BE28" }, // For Seattle teams

  // College Football Teams (from dummyData)
  "georgia bulldogs": { primary: "#BA0C2F", secondary: "#000000" },
  "texas longhorns": { primary: "#BF5700", secondary: "#FFFFFF" },
  texas: { primary: "#BF5700", secondary: "#FFFFFF" }, // Short name for Texas
  "michigan wolverines": { primary: "#00274C", secondary: "#FFCB05" },
  "alabama crimson tide": { primary: "#9E1B32", secondary: "#FFFFFF" },
  "oregon ducks": { primary: "#154733", secondary: "#FEE123" },
  "ohio state buckeyes": { primary: "#BB0000", secondary: "#666666" },
  "ohio state": { primary: "#BB0000", secondary: "#666666" }, // Short name for Ohio State
  "the ohio state university buckeyes": { primary: "#BB0000", secondary: "#666666" },
  "penn state nittany lions": { primary: "#041E42", secondary: "#FFFFFF" },
  "notre dame fighting irish": { primary: "#0C2340", secondary: "#C99700" },
  "notre dame": { primary: "#0C2340", secondary: "#C99700" },
  "clemson tigers": { primary: "#F66733", secondary: "#522D80" },
  clemson: { primary: "#F66733", secondary: "#522D80" }, // Short name for Clemson
  "lsu tigers": { primary: "#461D7C", secondary: "#FDD023" },
  "LSU tigers": { primary: "#461D7C", secondary: "#FDD023" },
  lsu: { primary: "#461D7C", secondary: "#FDD023" }, // Short name for LSU
  "louisiana state university tigers": { primary: "#461D7C", secondary: "#FDD023" },
  "florida state seminoles": { primary: "#782F40", secondary: "#CEB888" },
  "washington huskies": { primary: "#4B2E83", secondary: "#B7A57A" },
  "university of washington huskies": { primary: "#4B2E83", secondary: "#B7A57A" },
  "tennessee volunteers": { primary: "#FF8200", secondary: "#FFFFFF" },
  "oklahoma sooners": { primary: "#841617", secondary: "#FDF9D8" },
  "utah utes": { primary: "#CC0000", secondary: "#FFFFFF" },
  "ole miss rebels": { primary: "#CE1126", secondary: "#14213D" },
  "wisconsin badgers": { primary: "#C5050C", secondary: "#FFFFFF" },
  "miami hurricanes": { primary: "#F47321", secondary: "#046A38" },
  "miami fl": { primary: "#F47321", secondary: "#046A38" }, // Short name for Miami FL
  "north carolina tar heels": { primary: "#13294B", secondary: "#4B9CD3" },
  "iowa hawkeyes": { primary: "#FFCD00", secondary: "#000000" },
  "oklahoma state cowboys": { primary: "#FF7300", secondary: "#000000" },
  "kansas state wildcats": { primary: "#512888", secondary: "#FFFFFF" },
  "texas christian horned frogs": { primary: "#4D1979", secondary: "#A7A9AC" },
  "tcu horned frogs": { primary: "#4D1979", secondary: "#A7A9AC" },
  "TCU horned frogs": { primary: "#4D1979", secondary: "#A7A9AC" },
  "baylor bears": { primary: "#003015", secondary: "#FFB81C" },
  "texas a&m aggies": { primary: "#500000", secondary: "#FFFFFF" },
  "texas tech red raiders": { primary: "#CC0000", secondary: "#000000" }, // Add missing Texas Tech colors

  // Additional CFB duplicates for common variations
  bulldogs: { primary: "#BA0C2F", secondary: "#000000" },
  longhorns: { primary: "#BF5700", secondary: "#FFFFFF" },
  wolverines: { primary: "#00274C", secondary: "#FFCB05" },
  "crimson tide": { primary: "#9E1B32", secondary: "#FFFFFF" },
  ducks: { primary: "#154733", secondary: "#FEE123" },
  buckeyes: { primary: "#BB0000", secondary: "#666666" },
  "nitanny lions": { primary: "#041E42", secondary: "#FFFFFF" },
  "fighting irish": { primary: "#0C2340", secondary: "#C99700" },
  "cfb tigers": { primary: "#F66733", secondary: "#522D80" },
  LSU: { primary: "#461D7C", secondary: "#FDD023" },
  seminoles: { primary: "#782F40", secondary: "#CEB888" },
  huskies: { primary: "#4B2E83", secondary: "#B7A57A" },
  volunteers: { primary: "#FF8200", secondary: "#FFFFFF" },
  sooners: { primary: "#841617", secondary: "#FDF9D8" },
  utes: { primary: "#CC0000", secondary: "#FFFFFF" },
  rebels: { primary: "#CE1126", secondary: "#14213D" },
  badgers: { primary: "#C5050C", secondary: "#FFFFFF" },
  hurricanes: { primary: "#F47321", secondary: "#046A38" },
  UCF: { primary: "#000000", secondary: "#FFC904" },
  BYU: { primary: "#002E5D", secondary: "#FFFFFF" },
  SMU: { primary: "#C8102E", secondary: "#FFFFFF" },
  "tar heels": { primary: "#13294B", secondary: "#4B9CD3" },
  hawkeyes: { primary: "#FFCD00", secondary: "#000000" },
  "cfb cowboys": { primary: "#FF7300", secondary: "#000000" },
  wildcats: { primary: "#512888", secondary: "#FFFFFF" },
  "horned frogs": { primary: "#4D1979", secondary: "#A7A9AC" },
  TCU: { primary: "#4D1979", secondary: "#A7A9AC" },
  "cfb bears": { primary: "#003015", secondary: "#FFB81C" },
  aggies: { primary: "#500000", secondary: "#FFFFFF" },
  "red raiders": { primary: "#CC0000", secondary: "#000000" }, // Short name for Texas Tech

  // Additional CFB Teams
  "auburn tigers": { primary: "#0C2340", secondary: "#F26522" },
  "auburn university tigers": { primary: "#0C2340", secondary: "#F26522" },
  "florida gators": { primary: "#0021A5", secondary: "#FA4616" },
  "arkansas razorbacks": { primary: "#9D2235", secondary: "#FFFFFF" },
  "kentucky wildcats": { primary: "#0033A0", secondary: "#FFFFFF" },
  "south carolina gamecocks": { primary: "#73000A", secondary: "#000000" },
  gamecocks: { primary: "#73000A", secondary: "#000000" }, // Short name for South Carolina
  "missouri tigers": { primary: "#F1B82D", secondary: "#000000" },
  "mississippi state bulldogs": { primary: "#5D1725", secondary: "#FFFFFF" },
  "vanderbilt commodores": { primary: "#866D4B", secondary: "#000000" },
  "stanford cardinal": { primary: "#8C1515", secondary: "#FFFFFF" },
  "usc trojans": { primary: "#990000", secondary: "#FFCC00" },
  "USC trojans": { primary: "#990000", secondary: "#FFCC00" },
  "southern california trojans": { primary: "#990000", secondary: "#FFCC00" },
  "ucla bruins": { primary: "#2774AE", secondary: "#FFD100" },
  "UCLA bruins": { primary: "#2774AE", secondary: "#FFD100" },
  "university of california los angeles bruins": { primary: "#2774AE", secondary: "#FFD100" },
  "california golden bears": { primary: "#003262", secondary: "#FDB515" },
  "washington state cougars": { primary: "#981E32", secondary: "#C6BEB5" },
  "oregon state beavers": { primary: "#DC4405", secondary: "#000000" },
  "colorado buffaloes": { primary: "#CFB87C", secondary: "#000000" },
  "arizona wildcats": { primary: "#0C234B", secondary: "#AB0520" },
  "arizona state sun devils": { primary: "#8C1D40", secondary: "#FFC627" },
  "nebraska cornhuskers": { primary: "#E41C38", secondary: "#FEFDFA" },
  "minnesota golden gophers": { primary: "#7A0019", secondary: "#FFCC33" },
  "minnesota gophers": { primary: "#7A0019", secondary: "#FFCC33" },
  "illinois fighting illini": { primary: "#13294B", secondary: "#E84A27" },
  "northwestern wildcats": { primary: "#4E2A84", secondary: "#FFFFFF" },
  "purdue boilermakers": { primary: "#CEB888", secondary: "#000000" },
  "indiana hoosiers": { primary: "#990000", secondary: "#FFFFFF" },
  "maryland terrapins": { primary: "#E03A3E", secondary: "#FFD520" },
  "rutgers scarlet knights": { primary: "#CC0033", secondary: "#FFFFFF" },
  "michigan state spartans": { primary: "#18453B", secondary: "#FFFFFF" },
  "virginia cavaliers": { primary: "#232D4B", secondary: "#F84C1E" },
  "virginia tech hokies": { primary: "#861F41", secondary: "#E87722" },
  "nc state wolfpack": { primary: "#CC0000", secondary: "#FFFFFF" },
  "wake forest demon deacons": { primary: "#9E7E38", secondary: "#000000" },
  "duke blue devils": { primary: "#003087", secondary: "#FFFFFF" },
  "georgia tech yellow jackets": { primary: "#B3A369", secondary: "#003057" },
  "louisville cardinals": { primary: "#AD0000", secondary: "#000000" },
  "pittsburgh panthers": { primary: "#003594", secondary: "#FFB81C" },
  "syracuse orange": { primary: "#D44500", secondary: "#000E54" },
  "boston college eagles": { primary: "#98002E", secondary: "#CCAC00" },
  "west virginia mountaineers": { primary: "#002855", secondary: "#EAAA00" },
  "cincinnati bearcats": { primary: "#E00122", secondary: "#000000" },
  "university of cincinnati bearcats": { primary: "#E00122", secondary: "#000000" },
  "houston cougars": { primary: "#C8102E", secondary: "#FFFFFF" },
  "university of houston cougars": { primary: "#C8102E", secondary: "#FFFFFF" },
  "ucf knights": { primary: "#000000", secondary: "#FFC904" },
  "UCF knights": { primary: "#000000", secondary: "#FFC904" },
  "university of central florida knights": { primary: "#000000", secondary: "#FFC904" },
  "kansas jayhawks": { primary: "#0051BA", secondary: "#E8000D" },
  "iowa state cyclones": { primary: "#C8102E", secondary: "#F1BE48" },
  "byu cougars": { primary: "#002E5D", secondary: "#FFFFFF" },
  "BYU cougars": { primary: "#002E5D", secondary: "#FFFFFF" },
  "brigham young university cougars": { primary: "#002E5D", secondary: "#FFFFFF" },
  "smu mustangs": { primary: "#C8102E", secondary: "#FFFFFF" },
  "SMU mustangs": { primary: "#C8102E", secondary: "#FFFFFF" },
  "southern methodist university mustangs": { primary: "#C8102E", secondary: "#FFFFFF" },

  // Additional CFB duplicates for common variations
  auburn: { primary: "#0C2340", secondary: "#F26522" },
  gators: { primary: "#0021A5", secondary: "#FA4616" },
  razorbacks: { primary: "#9D2235", secondary: "#FFFFFF" },
  "mississippi state": { primary: "#5D1725", secondary: "#FFFFFF" },
  commodores: { primary: "#866D4B", secondary: "#000000" },
  cardinal: { primary: "#8C1515", secondary: "#FFFFFF" },
  trojans: { primary: "#990000", secondary: "#FFCC00" },
  bruins: { primary: "#2774AE", secondary: "#FFD100" },
  "golden bears": { primary: "#003262", secondary: "#FDB515" },
  cougars: { primary: "#981E32", secondary: "#C6BEB5" },
  beavers: { primary: "#DC4405", secondary: "#000000" },
  buffaloes: { primary: "#CFB87C", secondary: "#000000" },
  "sun devils": { primary: "#8C1D40", secondary: "#FFC627" },
  cornhuskers: { primary: "#E41C38", secondary: "#FEFDFA" },
  "golden gophers": { primary: "#7A0019", secondary: "#FFCC33" },
  "fighting illini": { primary: "#13294B", secondary: "#E84A27" },
  boilermakers: { primary: "#CEB888", secondary: "#000000" },
  hoosiers: { primary: "#990000", secondary: "#FFFFFF" },
  terrapins: { primary: "#E03A3E", secondary: "#FFD520" },
  "scarlet knights": { primary: "#CC0033", secondary: "#FFFFFF" },
  spartans: { primary: "#18453B", secondary: "#FFFFFF" },
  cavaliers: { primary: "#232D4B", secondary: "#F84C1E" },
  hokies: { primary: "#861F41", secondary: "#E87722" },
  wolfpack: { primary: "#CC0000", secondary: "#FFFFFF" },
  "demon deacons": { primary: "#9E7E38", secondary: "#000000" },
  "blue devils": { primary: "#003087", secondary: "#FFFFFF" },
  "yellow jackets": { primary: "#B3A369", secondary: "#003057" },
  bearcats: { primary: "#E00122", secondary: "#000000" },
  knights: { primary: "#000000", secondary: "#FFC904" },
  jayhawks: { primary: "#0051BA", secondary: "#E8000D" },
  cyclones: { primary: "#C8102E", secondary: "#F1BE48" },

  // MLB Teams (from dummyData)
  "new york yankees": { primary: "#132448", secondary: "#C4CED4" },
  yankees: { primary: "#132448", secondary: "#C4CED4" },
  "baltimore orioles": { primary: "#DF4601", secondary: "#000000" },
  "tampa bay rays": { primary: "#092C5C", secondary: "#8FBCE6" },
  "toronto blue jays": { primary: "#134A8E", secondary: "#1D2D5C" },
  "boston red sox": { primary: "#BD3039", secondary: "#0C2340" },
  "cleveland guardians": { primary: "#E31937", secondary: "#002B5C" },
  "minnesota twins": { primary: "#002B5C", secondary: "#D31145" },
  "detroit tigers": { primary: "#0C2340", secondary: "#FA4616" },
  "kansas city royals": { primary: "#004687", secondary: "#BD9B60" },
  "chicago white sox": { primary: "#27251F", secondary: "#C4CED4" },
  "white sox": { primary: "#27251F", secondary: "#C4CED4" },
  "houston astros": { primary: "#002D62", secondary: "#EB6E1F" },
  "seattle mariners": { primary: "#0C2C56", secondary: "#005C5C" },
  "texas rangers": { primary: "#003278", secondary: "#C0111F" },
  "los angeles angels": { primary: "#BA0021", secondary: "#003263" },
  "oakland athletics": { primary: "#003831", secondary: "#EFB21E" },
  "atlanta braves": { primary: "#CE1141", secondary: "#13274F" },
  "philadelphia phillies": { primary: "#E81828", secondary: "#002D72" },
  "new york mets": { primary: "#002D72", secondary: "#FF5910" },
  "miami marlins": { primary: "#00A3E0", secondary: "#EF3340" },
  "washington nationals": { primary: "#AB0003", secondary: "#14225A" },
  "milwaukee brewers": { primary: "#FFC52F", secondary: "#12284B" },
  "chicago cubs": { primary: "#0E3386", secondary: "#CC3433" },
  "st. louis cardinals": { primary: "#C41E3A", secondary: "#FEDB00" },
  "cincinnati reds": { primary: "#C6011F", secondary: "#000000" },
  "pittsburgh pirates": { primary: "#FDB827", secondary: "#27251F" },
  "los angeles dodgers": { primary: "#005A9C", secondary: "#EF3E42" },
  "san diego padres": { primary: "#2F241D", secondary: "#FFC425" },
  "arizona diamondbacks": { primary: "#A71930", secondary: "#E3D4A1" },
  "san francisco giants": { primary: "#FD5A1E", secondary: "#27251F" },
  "colorado rockies": { primary: "#33006F", secondary: "#C4CED4" },

  // Additional MLB duplicates for common variations
  orioles: { primary: "#DF4601", secondary: "#000000" },
  rays: { primary: "#092C5C", secondary: "#8FBCE6" },
  "blue jays": { primary: "#134A8E", secondary: "#1D2D5C" },
  "red sox": { primary: "#BD3039", secondary: "#0C2340" },
  guardians: { primary: "#E31937", secondary: "#002B5C" },
  twins: { primary: "#002B5C", secondary: "#D31145" },
  tigers: { primary: "#0C2340", secondary: "#FA4616" },
  royals: { primary: "#004687", secondary: "#BD9B60" },
  astros: { primary: "#002D62", secondary: "#EB6E1F" },
  mariners: { primary: "#0C2C56", secondary: "#005C5C" },
  rangers: { primary: "#003278", secondary: "#C0111F" },
  angels: { primary: "#BA0021", secondary: "#003263" },
  athletics: { primary: "#003831", secondary: "#EFB21E" },
  braves: { primary: "#CE1141", secondary: "#13274F" },
  phillies: { primary: "#E81828", secondary: "#002D72" },
  mets: { primary: "#002D72", secondary: "#FF5910" },
  marlins: { primary: "#00A3E0", secondary: "#EF3340" },
  nationals: { primary: "#AB0003", secondary: "#14225A" },
  brewers: { primary: "#FFC52F", secondary: "#12284B" },
  cubs: { primary: "#0E3386", secondary: "#CC3433" },
  "st. louis": { primary: "#C41E3A", secondary: "#FEDB00" },
  reds: { primary: "#C6011F", secondary: "#000000" },
  pirates: { primary: "#FDB827", secondary: "#27251F" },
  dodgers: { primary: "#005A9C", secondary: "#EF3E42" },
  padres: { primary: "#2F241D", secondary: "#FFC425" },
  diamondbacks: { primary: "#A71930", secondary: "#E3D4A1" },
  rockies: { primary: "#33006F", secondary: "#C4CED4" },

  // Golf Players
  "tiger woods": { primary: "#059669", secondary: "#FFFFFF" },
  "rory mcilroy": { primary: "#7C3AED", secondary: "#FFFFFF" },
}
